import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { PageFallback } from './PageFallback';
import { RequireAdmin, RequireAuth } from '../lib/auth';
import { PlatformRedirect } from './PlatformRedirect';

// As páginas editoriais públicas vieram da plataforma como estavam — o
// portal não tem variação visual delas.
const BlogIndexPage = lazy(() => import('@/pages/Blog/BlogIndexPage'));
const CloudPage = lazy(() => import('@/pages/Blog/CloudPage'));
const BlogPublicPage = lazy(() => import('@/pages/Blog/BlogPublicPage'));
const DevToolsPage = lazy(() => import('@/pages/DevTools/DevToolsPage'));
const PrivacyPolicy = lazy(() => import('@/pages/Legal/PrivacyPolicy'));
const TermsOfUse = lazy(() => import('@/pages/Legal/TermsOfUse'));

// As páginas próprias do portal (vitrine, login, área de membros, admin).
const BlogHomePage = lazy(() => import('../pages/Home/BlogHomePage'));
const BlogLoginPage = lazy(() => import('../pages/Login/BlogLoginPage'));
const BlogAuthCallbackPage = lazy(() => import('../pages/Login/BlogAuthCallbackPage'));
const BlogEmailConfirmPage = lazy(() => import('../pages/Login/BlogEmailConfirmPage'));
const CoursesPage = lazy(() => import('../pages/Cursos/CoursesPage'));
const CourseDetailPage = lazy(() => import('../pages/Cursos/CourseDetailPage'));
const AboutPage = lazy(() => import('../pages/Sobre/AboutPage'));
const MyCoursesPage = lazy(() => import('../pages/members/MyCoursesPage'));
const HistoryPage = lazy(() => import('../pages/members/HistoryPage'));
const PurchasesPage = lazy(() => import('../pages/members/PurchasesPage'));
const ResourcesPage = lazy(() => import('../pages/members/ResourcesPage'));
const CommunityPage = lazy(() => import('../pages/members/CommunityPage'));
const SettingsPage = lazy(() => import('../pages/members/SettingsPage'));
const AdminPage = lazy(() => import('../pages/Admin/AdminPage'));
const BlogNotFoundPage = lazy(() => import('../pages/NotFound/BlogNotFoundPage'));

/** Wrap a lazy element in Suspense with the content-shaped fallback. */
function page(element: ReactNode): ReactNode {
  return <Suspense fallback={<PageFallback />}>{element}</Suspense>;
}

/** Uma rota da área logada: Suspense dentro do portão de sessão. */
function gated(element: ReactNode): ReactNode {
  return <RequireAuth>{page(element)}</RequireAuth>;
}

/**
 * Uma rota do admin: sessão e papel. Logado sem o papel vê o 404 do portal — a
 * área não se revela (a Members API responde 404 do mesmo jeito).
 */
function adminOnly(element: ReactNode): ReactNode {
  return (
    <RequireAuth>
      <RequireAdmin notFound={page(<BlogNotFoundPage />)}>{page(element)}</RequireAdmin>
    </RequireAuth>
  );
}

/**
 * O contrato de rotas do portal Se Esforce (WS11): o editorial público nas
 * mesmas URLs de sempre, a home editorial em `/`, o login LOCAL do portal, o
 * catálogo e o "Sobre" públicos, a área de membros e o admin. Deep links da
 * plataforma (`/app*`, `/perfis*` e as rotas comerciais do console) não
 * existem aqui — viram redirect para shiba.ia.br.
 */
export const blogRoutes: RouteObject[] = [
  { path: '/', element: page(<BlogHomePage />) },
  { path: '/blog', element: page(<BlogIndexPage />) },
  { path: '/blog/communications', element: page(<CloudPage />) },
  { path: '/blog/p/:slug', element: page(<BlogPublicPage />) },
  { path: '/ferramentas', element: page(<DevToolsPage />) },
  { path: '/privacidade', element: page(<PrivacyPolicy />) },
  { path: '/privacy', element: <Navigate to="/privacidade" replace /> },
  { path: '/termos', element: page(<TermsOfUse />) },
  { path: '/terms', element: <Navigate to="/termos" replace /> },
  { path: '/login', element: page(<BlogLoginPage />) },
  {
    // Pública de propósito: o Hosted UI volta aqui com o código OAuth — atrás
    // do portão de sessão, o login travaria em loop.
    path: '/auth/callback',
    element: page(<BlogAuthCallbackPage />),
  },
  // Pública também: é o destino dos botões dos e-mails do Auth (convite,
  // senha, cadastro), aberto por quem ainda não tem sessão.
  { path: '/auth/confirm', element: page(<BlogEmailConfirmPage />) },
  // Os e-mails do Auth e os favoritos antigos ainda levam a /conta.
  { path: '/conta', element: <Navigate to="/meus-cursos" replace /> },
  { path: '/cursos', element: page(<CoursesPage />) },
  { path: '/curso/:slug', element: gated(<CourseDetailPage />) },
  { path: '/sobre', element: page(<AboutPage />) },
  { path: '/meus-cursos', element: gated(<MyCoursesPage />) },
  { path: '/historico', element: gated(<HistoryPage />) },
  { path: '/compras', element: gated(<PurchasesPage />) },
  { path: '/recursos', element: gated(<ResourcesPage />) },
  { path: '/comunidade', element: gated(<CommunityPage />) },
  { path: '/configuracoes', element: gated(<SettingsPage />) },
  { path: '/admin', element: adminOnly(<AdminPage />) },
  // Deep links antigos da plataforma → o equivalente em shiba.ia.br.
  { path: '/app/*', element: <PlatformRedirect /> },
  { path: '/perfis/*', element: <PlatformRedirect /> },
  { path: '/planos', element: <PlatformRedirect /> },
  { path: '/assinatura/*', element: <PlatformRedirect /> },
  { path: '*', element: page(<BlogNotFoundPage />) },
];

export const blogRouter = createBrowserRouter(blogRoutes);
